import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaEyeSlash, FaEye, FaEdit } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import '../css/UserManagement.css';
import { usersAPI } from '../utils/api';

export default function UserManagement() {
    const { token, user } = useContext(AuthContext);

    const [formData, setFormData] = useState({
        fullname: "",
        email: "",
        password: "",
        confirmPassword: "",
        role: "normal_user",
    });

    const [showCreatePasswords, setShowCreatePasswords] = useState({
        password: false,
        confirmPassword: false
    });

    const [users, setUsers] = useState([]);

    // State for edit modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({
        userId: "",
        newPassword: "",
        confirmNewPassword: ""
    });
    const [editingUser, setEditingUser] = useState(null);
    const [showPasswords, setShowPasswords] = useState({
        newPassword: false,
        confirmNewPassword: false
    });

    useEffect(() => {
        if (user?.role?.toLowerCase() === 'admin') {
            loadUsers();
        }
    }, [user]);

    if (user?.role?.toLowerCase() !== 'admin') {
        return (
            <div className="container mt-5 text-center">
                <h2>Access Denied</h2>
                <p>You do not have permission to view this page. Only administrators can manage users.</p>
            </div>
        );
    }



    // Load all users
    const loadUsers = async () => {
        try {
            const res = await usersAPI.getAll();
            setUsers(res.data); // backend returns an array directly
        } catch (err) {
            // console.error("Failed to load users:", err);
        }
    };


    // Handle form input
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Handle edit form input
    const handleEditChange = (e) => {
        setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
    };

    // Toggle password visibility for create form
    const toggleCreatePasswordVisibility = (field) => {
        setShowCreatePasswords({ ...showCreatePasswords, [field]: !showCreatePasswords[field] });
    };

    // Toggle password visibility for edit modal
    const togglePasswordVisibility = (field) => {
        setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
    };

    // Open edit modal
    const openEditModal = (user) => {
        setEditingUser(user);
        setEditFormData({
            userId: user.id,
            newPassword: "",
            confirmNewPassword: ""
        });
        setShowPasswords({
            newPassword: false,
            confirmNewPassword: false
        });
        setShowEditModal(true);
    };

    // Close edit modal
    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingUser(null);
        setEditFormData({
            userId: "",
            newPassword: "",
            confirmNewPassword: ""
        });
        setShowPasswords({
            newPassword: false,
            confirmNewPassword: false
        });
    };

    // Create user
    const createUser = async () => {
        if (
            !formData.fullname ||
            !formData.email ||
            !formData.password ||
            formData.password !== formData.confirmPassword
        ) {
            alert("Please fill all fields and match passwords.");
            return;
        }

        try {
            await usersAPI.create({
                full_name: formData.fullname,
                email: formData.email,
                password: formData.password,
                role: formData.role,
            });

            alert("User created successfully!");

            setFormData({
                fullname: "",
                email: "",
                password: "",
                confirmPassword: "",
                role: "normal_user",
            });

            setShowCreatePasswords({
                password: false,
                confirmPassword: false
            });

            loadUsers();
        } catch (err) {
            // console.error(err);
            alert(err.response?.data?.message || "Error creating user");
        }
    };

    // Update user password
    const updateUserPassword = async () => {
        if (!editFormData.newPassword || editFormData.newPassword !== editFormData.confirmNewPassword) {
            alert("New passwords must match and not be empty.");
            return;
        }

        // Optional: Add password strength validation
        if (editFormData.newPassword.length < 6) {
            alert("Password must be at least 6 characters long.");
            return;
        }

        try {
            await usersAPI.updatePassword(editFormData.userId, {
                newPassword: editFormData.newPassword
            });

            alert("Password updated successfully!");
            closeEditModal();
            loadUsers();
        } catch (err) {
            // console.error(err);
            alert(err.response?.data?.message || "Error updating password");
        }
    };

    // Deactivate user (soft delete - hide from login)
    const deactivateUser = async (id) => {
        if (!window.confirm("Are you sure you want to deactivate this user?")) return;

        try {
            await usersAPI.deactivate(id);

            alert("User deactivated successfully!");
            loadUsers();
        } catch (err) {
            // console.error(err);
            alert(err.response?.data?.message || "Failed to deactivate user");
        }
    };

    // Activate user (reactivate deactivated user)
    const activateUser = async (id) => {
        if (!window.confirm("Are you sure you want to activate this user?")) return;

        try {
            await usersAPI.activate(id);

            alert("User activated successfully!");
            loadUsers();
        } catch (err) {
            // console.error(err);
            alert(err.response?.data?.message || "Failed to activate user");
        }
    };

    // Permanently delete user
    const deleteUser = async (id) => {
        if (!window.confirm("Are you sure you want to permanently delete this user? This action cannot be undone.")) return;

        try {
            await usersAPI.delete(id);

            alert("User deleted permanently!");
            loadUsers();
        } catch (err) {
            // console.error(err);
            alert(err.response?.data?.message || "Failed to delete user");
        }
    };

    return (
        <div className="p-4 w-100">
            <h3 className="fw-bold">Create New User</h3>
            <p>Fill out the form below to add a new user to the system.</p>

            {/* Create User Form */}
            <div className="border rounded bg-white p-4">
                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">Full Name</label>
                        <input
                            type="text"
                            className="form-control"
                            name="fullname"
                            value={formData.fullname}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">Email</label>
                        <input
                            type="email"
                            className="form-control"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                {/* Password fields */}
                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">Password</label>
                        <div className="input-group">
                            <input
                                type={showCreatePasswords.password ? "text" : "password"}
                                className="form-control"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                            />
                            <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => toggleCreatePasswordVisibility('password')}
                            >
                                {showCreatePasswords.password ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">Confirm Password</label>
                        <div className="input-group">
                            <input
                                type={showCreatePasswords.confirmPassword ? "text" : "password"}
                                className="form-control"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                            />
                            <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => toggleCreatePasswordVisibility('confirmPassword')}
                            >
                                {showCreatePasswords.confirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* User Role */}
                <div className="row mb-4">
                    <div className="col-md-6">
                        <label className="form-label">User Role</label>
                        <select
                            className="form-select"
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                        >
                            <option value="normal_user">Normal User</option>
                            <option value="purchase_manager">Purchase Manager</option>
                            <option value="sales_manager">Sales Manager</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                </div>

                {/* Buttons */}
                <div className="d-flex justify-content-end">
                    <button className="btn btn-secondary me-2">Cancel</button>
                    <button className="btn btn-primary" onClick={createUser}>
                        Create User
                    </button>
                </div>
            </div>

            {/* Existing Users */}
            <h4 className="fw-bold mt-5 mb-3">Existing Users</h4>

            <div className="table-responsive bg-white border rounded shadow-sm">
                <table className="table table-hover align-middle mb-0">
                    <thead className="table-light text-muted">
                        <tr>
                            <th className="text-nowrap">User's Full Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th className="text-end text-nowrap">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.id} style={{ opacity: user.is_active ? 1 : 0.6 }}>
                                <td>{user.full_name}</td>
                                <td>{user.email}</td>
                                <td className="text-capitalize text-muted">{user.role?.replace('_', ' ')}</td>
                                <td>
                                    <span className={`badge ${user.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                        {user.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    <div className="d-flex justify-content-end gap-2 flex-wrap flex-md-nowrap">
                                        {user.is_active ? (
                                            <>
                                                <button
                                                    className="btn btn-info btn-sm text-white"
                                                    onClick={() => openEditModal(user)}
                                                    title="Edit Password"
                                                >
                                                    <FaEdit className="me-1" /> Edit
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => deactivateUser(user.id)}
                                                    title="Deactivate User"
                                                >
                                                    Deactivate
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => deleteUser(user.id)}
                                                    title="Permanently remove user"
                                                >
                                                    Delete
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    className="btn btn-success btn-sm"
                                                    onClick={() => activateUser(user.id)}
                                                >
                                                    Activate
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => deleteUser(user.id)}
                                                    title="Permanently remove user"
                                                >
                                                    Delete
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Bootstrap Modal for Password Editing */}
            {showEditModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Password - {editingUser?.full_name}</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={closeEditModal}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-12 mb-3">
                                        <label className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className="form-control modal-input-fix"
                                            value={editingUser?.email || ''}
                                            disabled
                                        />
                                        <small className="text-muted">This is for reference only</small>
                                    </div>

                                    <div className="col-12 mb-3">
                                        <label className="form-label">New Password</label>
                                        <div className="input-group">
                                            <input
                                                type={showPasswords.newPassword ? "text" : "password"}
                                                className="form-control modal-input-fix"
                                                name="newPassword"
                                                value={editFormData.newPassword}
                                                onChange={handleEditChange}
                                                placeholder="Enter new password"
                                            />
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => togglePasswordVisibility('newPassword')}
                                            >
                                                {showPasswords.newPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="col-12 mb-3">
                                        <label className="form-label">Confirm New Password</label>
                                        <div className="input-group">
                                            <input
                                                type={showPasswords.confirmNewPassword ? "text" : "password"}
                                                className="form-control modal-input-fix"
                                                name="confirmNewPassword"
                                                value={editFormData.confirmNewPassword}
                                                onChange={handleEditChange}
                                                placeholder="Confirm new password"
                                            />
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => togglePasswordVisibility('confirmNewPassword')}
                                            >
                                                {showPasswords.confirmNewPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={closeEditModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={updateUserPassword}
                                >
                                    Update Password
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}